import { useCallback, useMemo, useRef, useState } from 'react';
import {
  encryptFileStream,
  encryptMetadata,
  generateSecret,
  toFileDescriptor
} from '@services/crypto';
import { getApiBaseForRegion } from '@services/apiBase';
import { computeSaltedPasswordHash } from '@services/passwordHash';
import { buildShareLink } from '@utils/shareLink';
import { trackStreamProgress } from '@utils/progressStream';
import { generateUploadAdminPassword } from '@utils/uploadAdminPassword';
import { UploadManagementService } from '@services/uploadManagement';
import { isBenignTransferError } from '@utils/abort';
import { trackException, trackFileUploadConversion } from '@services/telemetry';
import {
  createUploadSession,
  getFileUploadUrl,
  abortUpload,
  requestUploadId,
  UploadLimitError,
  UploadSizeMismatchError,
  preflightUpload,
  markUploadEnded,
  createWorkerMultipartUpload,
  uploadWorkerMultipartPart,
  completeWorkerMultipartUpload,
  uploadAzureBlock,
  commitAzureBlockList
} from '../services/api';
import { FileDescriptor, UploadMetadata, UploadProgress } from '@types';

interface CreateUploadOptions {
  hasPassword?: boolean;
  password?: string;
  maxDownloads?: number;
  notifyByEmail?: boolean;
  regionKey?: string;
  brandingProfileId?: string;
}

interface UseUploaderResult {
  progress: UploadProgress[];
  isUploading: boolean;
  error?: string;
  uploadLimitError?: UploadLimitError;
  uploadSizeMismatchError?: UploadSizeMismatchError;
  createUpload: (
    files: File[],
    retentionPeriodCode: string,
    expiresAtIso: string,
    options?: CreateUploadOptions
  ) => Promise<string | undefined>;
  resolveDescriptor: (fileId: string) => FileDescriptor | undefined;
  cancel: () => void;
  lastShareLink?: string;
  clearErrors: () => void;
}

export function useUploader(): UseUploaderResult {
  const [progressMap, setProgressMap] = useState<Map<string, UploadProgress>>(new Map());
  const [descriptorMap, setDescriptorMap] = useState<Map<string, FileDescriptor>>(new Map());
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>();
  const [uploadLimitError, setUploadLimitError] = useState<UploadLimitError>();
  const [uploadSizeMismatchError, setUploadSizeMismatchError] = useState<UploadSizeMismatchError>();
  const [currentUploadId, setCurrentUploadId] = useState<string>();
  const [lastShareLink, setLastShareLink] = useState<string | undefined>(undefined);
  const cancelRequestedRef = useRef(false);
  const listenersRef = useRef<{
    onBeforeUnload?: () => void;
    onPageHide?: () => void;
  }>({});

  const progress = useMemo(() => Array.from(progressMap.values()), [progressMap]);

  const updateProgress = useCallback(
    (fileId: string, value: UploadProgress | ((current: UploadProgress) => UploadProgress)) => {
      setProgressMap((prev) => {
        const next = new Map(prev);
        const existing =
          prev.get(fileId) ??
          ({
            fileId,
            transferredBytes: 0,
            status: 'pending'
          } as UploadProgress);

        const nextValue = typeof value === 'function' ? value(existing) : value;
        next.set(fileId, nextValue);
        return next;
      });
    },
    []
  );

  const createUpload = useCallback(
    async (files: File[], retentionPeriodCode: string, expiresAtIso: string, options?: CreateUploadOptions) => {
      if (!files.length) {
        throw new Error('Select at least one file');
      }

      // Calculate total upload size
      const totalUploadSize = files.reduce((total, file) => total + (file.size || 0), 0);

      setIsUploading(true);
      setError(undefined);
      setUploadLimitError(undefined);
      setProgressMap(new Map());
      setDescriptorMap(new Map());
      let cleanupUnloadListeners: (() => void) | undefined;

      try {
        const apiBaseOverride = options?.regionKey ? getApiBaseForRegion(options.regionKey) : undefined;

        await preflightUpload({
          totalUploadSize,
          fileCount: files.length
        }, apiBaseOverride);

        const uploadId = await requestUploadId(
          retentionPeriodCode,
          options?.brandingProfileId,
          apiBaseOverride,
          options?.regionKey,
          totalUploadSize
        );
        const secret = generateSecret();
        setCurrentUploadId(uploadId);
        cancelRequestedRef.current = false;

        const descriptors = files.map((file) => toFileDescriptor(file, crypto.randomUUID()));
        setDescriptorMap(new Map(descriptors.map((descriptor) => [descriptor.id, descriptor])));
        const metadata = buildMetadata(descriptors, options?.hasPassword === true);
        const metadataCiphertext = await encryptMetadata(metadata, secret, uploadId);
        setLastShareLink(undefined);

        // Generate admin password for this upload
        const uploadAdminPassword = await generateUploadAdminPassword();

        let saltedPasswordHash: string | undefined;
        if (options?.hasPassword && options.password) {
          saltedPasswordHash = await computeSaltedPasswordHash(uploadId, options.password);
        }

        await createUploadSession({
          uploadId,
          metadataCiphertext,
          fileIds: descriptors.map((d) => d.id),
          totalUploadSize, // Include total upload size in bytes
          expiresAtIso,
          retentionPeriodCode,
          hasPassword: options?.hasPassword === true ? true : undefined,
          saltedPasswordHash,
          maxDownloads: options?.maxDownloads,
          notifyByEmail: options?.notifyByEmail === true ? true : undefined,
          brandingProfileId: options?.brandingProfileId,
          uploadAdminPassword,
        }, apiBaseOverride);

        // Ensure cleanup if the tab is closed or navigated away before completion
        let abortFired = false;
        const fireAbort = () => {
          if (!abortFired) {
            abortFired = true;
            abortUpload(uploadId, apiBaseOverride);
          }
        };
        const onBeforeUnload = () => fireAbort();
        const onPageHide = () => fireAbort();
        listenersRef.current = { onBeforeUnload, onPageHide };
        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('pagehide', onPageHide);
        cleanupUnloadListeners = () => {
          window.removeEventListener('beforeunload', onBeforeUnload);
          window.removeEventListener('pagehide', onPageHide);
          if (listenersRef.current.onBeforeUnload === onBeforeUnload) {
            listenersRef.current = {};
          }
        };

        await Promise.all(
          files.map(async (file, idx) => {
            const descriptor = descriptors[idx];
            updateProgress(descriptor.id, {
              fileId: descriptor.id,
              status: 'uploading',
              transferredBytes: 0,
              totalBytes: file.size || undefined
            });

            const doStreamingUpload = async () => {
              const totalBytes = file.size || undefined;
              let uploadedBytes = 0;

              const reportProgress = (bytes: number) => {
                if (cancelRequestedRef.current) return;
                uploadedBytes = bytes;
                updateProgress(descriptor.id, (current: UploadProgress) => ({
                  ...current,
                  transferredBytes: Math.min(bytes, totalBytes ?? bytes),
                  totalBytes
                }));
              };

              if (shouldUseWorkerMultipartUpload(uploadId)) {
                await uploadViaWorkerMultipart(file, uploadId, descriptor.id, secret, reportProgress, cancelRequestedRef);
              } else {
                const uploadUrl = await getFileUploadUrl(uploadId, descriptor.id);

                const headers: Record<string, string> = {
                  'Content-Type': 'application/octet-stream'
                };

                const url = new URL(uploadUrl);
                const isLocalhost = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
                const isDevMinio = isLocalhost && (url.port === '19000' || url.port === '20000');

                // In production the upload URL is the Cloudflare Worker proxy
                // (s<region>.sendallfiles.com/?u=<escaped Azure SAS>), so the Azure
                // markers (sv, blob host) live inside the `u` param, not at the top
                // level. Detect both the proxied form and a raw SAS (local Azurite).
                const proxiedSas = url.searchParams.get('u');
                const isAzureSas =
                  url.searchParams.has('sv') ||
                  (proxiedSas != null && proxiedSas.includes('.blob.core.windows.net'));

                let response: Response | undefined;

                if (isDevMinio) {
                  const retryStream = await encryptFileStream(file, secret, {
                    uploadId,
                    fileId: descriptor.id
                  });
                  const buffered = await new Response(retryStream).blob();

                  response = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers,
                    body: buffered,
                    mode: 'cors'
                  });
                  uploadedBytes = buffered.size;
                } else if (isAzureSas) {
                  await uploadViaAzureBlockBlob(
                    file,
                    uploadUrl,
                    uploadId,
                    descriptor.id,
                    secret,
                    reportProgress,
                    cancelRequestedRef
                  );
                  uploadedBytes = file.size;
                } else {
                  const sourceStream = await encryptFileStream(file, secret, {
                    uploadId,
                    fileId: descriptor.id
                  });

                  const streamWithProgress = trackStreamProgress(sourceStream, (bytes) => {
                    reportProgress(bytes);
                  });

                  const requestInit: RequestInit & { duplex: 'half' } = {
                    method: 'PUT',
                    headers,
                    body: streamWithProgress,
                    // Required by Chromium when sending a ReadableStream as body
                    duplex: 'half',
                    mode: 'cors'
                  };

                  try {
                    response = await fetch(uploadUrl, requestInit);
                  } catch (err) {
                    if (cancelRequestedRef.current) {
                      throw err;
                    }

                    const retryStream = await encryptFileStream(file, secret, {
                      uploadId,
                      fileId: descriptor.id
                    });
                    const buffered = await new Response(retryStream).blob();
                    response = await fetch(uploadUrl, {
                      method: 'PUT',
                      headers,
                      body: buffered,
                      mode: 'cors'
                    });
                    uploadedBytes = buffered.size;
                  }
                }

                if (!isAzureSas) {
                  if (!response) {
                    throw new Error(`Failed to upload file ${descriptor.id}: no response received`);
                  }

                  if (!response.ok) {
                    const text = await response.text().catch(() => response.statusText);
                    throw new Error(`Failed to upload file ${descriptor.id}: ${response.status} ${text}`);
                  }
                }
              }

              // Ensure progress reflects full size after completion
              updateProgress(descriptor.id, (current: UploadProgress) => ({
                ...current,
                transferredBytes: file.size || uploadedBytes,
                totalBytes: file.size || uploadedBytes
              }));
            };

            if (!cancelRequestedRef.current) {
              await doStreamingUpload();
            }

            if (!cancelRequestedRef.current) {
              updateProgress(descriptor.id, (current: UploadProgress) => ({
                ...current,
                status: 'complete',
                transferredBytes: current.totalBytes ?? file.size ?? current.transferredBytes
              }));
            }
          })
        );

        const shareLink = buildShareLink(uploadId, secret);
        // Always clean listeners first
        cleanupUnloadListeners();
        // Do not surface a link if cancel was requested
        if (cancelRequestedRef.current) {
          setLastShareLink(undefined);
          throw new Error('Upload canceled');
        }
        setLastShareLink(shareLink);

        // Count the conversion only after every file has been uploaded and the share link exists.
        // Google Ads tracking is best-effort and must never turn a successful upload into an error.
        try {
          await trackFileUploadConversion(uploadId, totalUploadSize);
        } catch {
          // ignore
        }

        // Signal the very end of a successful upload (all bytes are now in storage) so the backend
        // can record an UploadEnded event for the success/failure trend. Best-effort: never blocks
        // or fails the upload.
        void markUploadEnded(uploadId, apiBaseOverride);

        // Save upload information to local storage
        try {
          const uploadInfo = {
            uploadId,
            shareableLink: shareLink,
            createdAt: new Date().toISOString(),
            files: files.map(file => ({
              id: descriptors.find(d => d.name === file.name)?.id || '',
              name: file.name,
              size: file.size,
              contentType: file.type
            })),
            hostingRegion: options?.regionKey || 'default',
            maxDownloads: (options?.maxDownloads ?? null) as number | null,
            isPasswordProtected: options?.hasPassword === true,
            emailNotification: options?.notifyByEmail === true,
            expiryDate: expiresAtIso,
            uploadAdminPassword,
            brandingProfileId: options?.brandingProfileId
          };
          
          await UploadManagementService.saveUpload(uploadInfo);
        } catch (error) {
          console.error('Failed to save upload information:', error);
          // Don't fail the upload if saving to local storage fails
        }
        
        return shareLink;
      } catch (uploadError) {
        if (uploadError instanceof UploadLimitError) {
          setUploadLimitError(uploadError);
          // Add small delay to ensure React processes the state update
          await new Promise(resolve => setTimeout(resolve, 0));
          return undefined;
        }
        
        if (uploadError instanceof UploadSizeMismatchError) {
          setUploadSizeMismatchError(uploadError);
          throw uploadError;
        }
        
        const message =
          uploadError instanceof Error ? uploadError.message : 'Unable to complete upload';
        if (!cancelRequestedRef.current) {
          setError(message);
          if (isBenignTransferError(uploadError)) {
            // The user's connection dropped mid-upload. Client-side and not backend-actionable, so
            // log it instead of reporting to telemetry (it was only adding noise to the alert).
            console.error('Upload failed (client-side, not reported to telemetry):', uploadError);
          } else {
            // Report genuine upload failures (not cancels / limit / size-mismatch, handled above) so
            // client-side upload breakage is visible in telemetry rather than only inferred from stats.
            trackException(uploadError, { context: 'upload' });
          }
          throw uploadError;
        }
        throw new Error('Upload canceled');
      } finally {
        cleanupUnloadListeners?.();
        setIsUploading(false);
        setCurrentUploadId(undefined);
      }
    },
    [updateProgress]
  );

  const cancel = useCallback(() => {
    const id = currentUploadId;
    cancelRequestedRef.current = true;
    if (id) {
      abortUpload(id);
    }
    const { onBeforeUnload, onPageHide } = listenersRef.current;
    if (onBeforeUnload) window.removeEventListener('beforeunload', onBeforeUnload);
    if (onPageHide) window.removeEventListener('pagehide', onPageHide);
    listenersRef.current = {};
    setProgressMap(new Map());
    setDescriptorMap(new Map());
    setError(undefined);
    setUploadLimitError(undefined);
    setUploadSizeMismatchError(undefined);
    setIsUploading(false);
    setCurrentUploadId(undefined);
    setLastShareLink(undefined);
  }, [currentUploadId]);

  const clearErrors = useCallback(() => {
    setError(undefined);
    setUploadLimitError(undefined);
    setUploadSizeMismatchError(undefined);
  }, []);

  return {
    progress,
    isUploading,
    error,
    uploadLimitError,
    uploadSizeMismatchError,
    createUpload,
    resolveDescriptor: (fileId: string) => descriptorMap.get(fileId),
    cancel,
    lastShareLink,
    clearErrors
  };
}

function buildMetadata(files: FileDescriptor[], hasPassword?: boolean): UploadMetadata {
  const metadata: UploadMetadata = {
    createdAt: new Date().toISOString(),
    files
  };

  if (hasPassword) {
    metadata.hasPassword = true;
  }

  return metadata;
}

// Upload id is now generated by the backend via requestUploadId()

// Cloudflare R2 requires all non-final multipart parts to have the same size.
// Use a fixed 5MB part size for all non-final parts.
const MULTIPART_PART_SIZE_BYTES = 5 * 1024 * 1024;
const MULTIPART_MAX_RETRIES = 5;
const MULTIPART_INITIAL_RETRY_DELAY_MS = 1000;
const AZURE_BLOCK_SIZE_BYTES = MULTIPART_PART_SIZE_BYTES;

function shouldUseWorkerMultipartUpload(uploadId: string): boolean {
  // Determine upload method based on the uploadId prefix (storage region)
  // not the current hostname, since user might be on one region but uploading to another
  
  const prefix = uploadId && uploadId.length >= 4 ? uploadId.slice(0, 4).toLowerCase() : '';
  
  // R2 regions use worker multipart upload API
  const R2_REGIONS = ['weur', 'enam', 'apac', 'ocea'];
  if (R2_REGIONS.includes(prefix)) {
    return true;
  }
  
  // Azure regions (swno, cace, brso, etc.) use Azure Block Blob API with SAS URLs
  // All other regions default to Azure Block Blob API
  return false;
}

function getWorkerMultipartBaseUrl(uploadId: string): string {
  const id = uploadId ?? '';
  const prefix = id.length >= 4 ? id.slice(0, 4).toLowerCase() : '';

  if (prefix) {
    return `https://s${prefix}.sendallfiles.com/upload`;
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    const match = /^([a-z0-9]+)\.sendallfiles\.com$/.exec(host);
    const regionFromHost = match && match[1] ? match[1] : 'swno';
    return `https://s${regionFromHost}.sendallfiles.com/upload`;
  }

  return 'https://sswno.sendallfiles.com/upload';
}

async function uploadViaWorkerMultipart(
  file: File,
  uploadId: string,
  fileId: string,
  secret: string,
  reportProgress: (bytes: number) => void,
  cancelRequestedRef: { current: boolean }
): Promise<void> {
  const workerBase = getWorkerMultipartBaseUrl(uploadId);

  const r2UploadId = await createWorkerMultipartUpload(workerBase, uploadId, fileId);

  const encryptedStream = await encryptFileStream(file, secret, {
    uploadId,
    fileId
  });
  const reader = encryptedStream.getReader();

  const uploadedParts: { etag: string; partNumber: number }[] = [];
  let globalBytes = 0;
  let partNumber = 1;
  let partChunks: BlobPart[] = [];
  let partSize = 0;

  const flushPart = async () => {
    if (partSize === 0) {
      return;
    }

    const blob = new Blob(partChunks, { type: 'application/octet-stream' });

    if (cancelRequestedRef.current) {
        throw new Error('Upload canceled');
    }

    // Since we moved retry logic to api.ts (optional) or we can implement basic retry loop here calling api
    // api.ts uploadWorkerMultipartPart performs a simple fetch. If we want retries we need them here or in api.ts
    // The previous implementation had retries here. I should probably implement retries here calling the api function.
    // Actually, I can rely on the error thrown by api.ts and catch/retry here.
    
    // For simplicity, let's just wrap the api call in the retry loop.
    let attempt = 0;
    let delayMs = 1000;
    const MAX_RETRIES = 5;

    while (true) {
        try {
            const etag = await uploadWorkerMultipartPart(workerBase, uploadId, fileId, r2UploadId, partNumber, blob);
            uploadedParts.push({ etag, partNumber });
            partNumber += 1;
            partChunks = [];
            partSize = 0;
            return;
        } catch (error) {
            if (attempt >= MAX_RETRIES - 1) {
                throw error;
            }
            if (cancelRequestedRef.current) throw new Error('Upload canceled');
            attempt++;
            if (delayMs > 0) {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
                delayMs *= 2;
            }
        }
    }
  };

  while (true) {
    if (cancelRequestedRef.current) {
      throw new Error('Upload canceled');
    }

    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    if (!value || value.length === 0) {
      continue;
    }

    let remaining = value;
    while (remaining.byteLength > 0) {
      const remainingCapacity = MULTIPART_PART_SIZE_BYTES - partSize;
      const chunkSize = Math.min(remainingCapacity, remaining.byteLength);
      const chunk =
        chunkSize === remaining.byteLength ? remaining : remaining.subarray(0, chunkSize);

      partChunks.push(chunk as unknown as BlobPart);
      partSize += chunkSize;
      globalBytes += chunkSize;
      reportProgress(globalBytes);

      if (partSize >= MULTIPART_PART_SIZE_BYTES) {
        await flushPart();
      }

      if (chunkSize === remaining.byteLength) {
        remaining = new Uint8Array(0);
      } else {
        remaining = remaining.subarray(chunkSize);
      }
    }
  }

  if (partSize > 0) {
    await flushPart();
  }

  if (uploadedParts.length === 0) {
    return;
  }

  if (cancelRequestedRef.current) {
    throw new Error('Upload canceled');
  }

  // Complete multipart
  let completeAttempt = 0;
  let completeDelayMs = 1000;
  const MAX_RETRIES = 5;

  while (true) {
    if (cancelRequestedRef.current) {
      throw new Error('Upload canceled');
    }

    try {
      await completeWorkerMultipartUpload(workerBase, uploadId, fileId, r2UploadId, uploadedParts);
      return;
    } catch (error) {
      if (completeAttempt >= MAX_RETRIES - 1) {
        throw error;
      }
      completeAttempt++;
      if (completeDelayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, completeDelayMs));
        completeDelayMs *= 2;
      }
    }
  }
}

function buildAzureUrl(baseUrl: string, extraParams: Record<string, string>): string {
  const url = new URL(baseUrl);

  // When the upload goes through the Cloudflare Worker proxy
  // (s<region>.sendallfiles.com/?u=<sas>), the worker only forwards the `u`
  // target to Azure and ignores any other top-level query params. So the Azure
  // block params (comp, blockid, ...) must be set INSIDE the `u` SAS URL.
  const proxiedSas = url.searchParams.get('u');
  if (proxiedSas != null) {
    const target = new URL(proxiedSas);
    Object.entries(extraParams).forEach(([key, value]) => {
      target.searchParams.set(key, value);
    });
    url.searchParams.set('u', target.toString());
    return url.toString();
  }

  // Direct SAS URL (e.g. local Azurite): set the params on the URL itself.
  Object.entries(extraParams).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  return url.toString();
}

function toAzureBlockId(partNumber: number): string {
  const id = partNumber.toString().padStart(6, '0');
  return btoa(id);
}

async function uploadViaAzureBlockBlob(
  file: File,
  uploadUrl: string,
  uploadId: string,
  fileId: string,
  secret: string,
  reportProgress: (bytes: number) => void,
  cancelRequestedRef: { current: boolean }
): Promise<void> {
  const encryptedStream = await encryptFileStream(file, secret, {
    uploadId,
    fileId
  });
  const reader = encryptedStream.getReader();

  const blockIds: string[] = [];
  let partChunks: BlobPart[] = [];
  let partSize = 0;
  let partNumber = 0;
  let uploadedBytes = 0;

  const flushBlock = async () => {
    if (partSize === 0) {
      return;
    }

    const blockId = toAzureBlockId(partNumber);
    partNumber += 1;

    const blockUrl = buildAzureUrl(uploadUrl, {
      comp: 'block',
      blockid: blockId
    });

    const blob = new Blob(partChunks, { type: 'application/octet-stream' });

    // Retry loop for block upload
    let attempt = 0;
    let delayMs = 1000;
    const MAX_RETRIES = 5;

    while (true) {
        if (cancelRequestedRef.current) throw new Error('Upload canceled');
        try {
            await uploadAzureBlock(blockUrl, blob);
            break;
        } catch (error) {
            if (attempt >= MAX_RETRIES - 1) throw error;
            attempt++;
            await new Promise(resolve => setTimeout(resolve, delayMs));
            delayMs *= 2;
        }
    }

    blockIds.push(blockId);
    partChunks = [];
    partSize = 0;
  };

  while (true) {
    if (cancelRequestedRef.current) {
      throw new Error('Upload canceled');
    }

    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    if (!value || value.length === 0) {
      continue;
    }

    let remaining = value;
    while (remaining.byteLength > 0) {
      const remainingCapacity = AZURE_BLOCK_SIZE_BYTES - partSize;
      const chunkSize = Math.min(remainingCapacity, remaining.byteLength);
      const chunk = chunkSize === remaining.byteLength ? remaining : remaining.subarray(0, chunkSize);

      partChunks.push(chunk as unknown as BlobPart);
      partSize += chunkSize;
      uploadedBytes += chunkSize;
      reportProgress(uploadedBytes);

      if (partSize >= AZURE_BLOCK_SIZE_BYTES) {
        await flushBlock();
      }

      if (chunkSize === remaining.byteLength) {
        remaining = new Uint8Array(0);
      } else {
        remaining = remaining.subarray(chunkSize);
      }
    }
  }

  if (partSize > 0) {
    await flushBlock();
  }

  // If the file is empty, create an empty block list to finalize the blob
  if (blockIds.length === 0) {
    const blockId = toAzureBlockId(0);
    blockIds.push(blockId);
    const blockUrl = buildAzureUrl(uploadUrl, {
      comp: 'block',
      blockid: blockId
    });
    // Retry loop for empty block
    let attempt = 0;
    let delayMs = 1000;
    const MAX_RETRIES = 5;
    while(true) {
        if(cancelRequestedRef.current) throw new Error('Upload canceled');
        try {
            await uploadAzureBlock(blockUrl, new Blob([], { type: 'application/octet-stream' }));
            break;
        } catch(error) {
            if(attempt >= MAX_RETRIES - 1) throw error;
            attempt++;
            await new Promise(resolve => setTimeout(resolve, delayMs));
            delayMs *= 2;
        }
    }
  }

  const blockListXml = `<BlockList>${blockIds.map((id) => `<Latest>${id}</Latest>`).join('')}</BlockList>`;
  const commitUrl = buildAzureUrl(uploadUrl, { comp: 'blocklist' });

  // Retry loop for commit
  let attempt = 0;
  let delayMs = 1000;
  const MAX_RETRIES = 5;
  while(true) {
      if(cancelRequestedRef.current) throw new Error('Upload canceled');
      try {
          await commitAzureBlockList(commitUrl, blockListXml);
          return;
      } catch(error) {
          if(attempt >= MAX_RETRIES - 1) throw error;
          attempt++;
          await new Promise(resolve => setTimeout(resolve, delayMs));
          delayMs *= 2;
      }
  }
}
